import "./ImageSelector.scss";

import React, { useCallback, useState } from "react";
import clsx from "clsx";
import { toast } from "react-toastify";

import * as ErrorUtils from "../../../lib/util/ErrorUtils";

import Modal from "../../container/Modal/Modal";
import DragAndDropFile from "../DragAndDropFile/DragAndDropFile";
import LoadingSpinner from "../../common/LoadingSpinner/LoadingSpinner";
import MenuDropdown from "../../nav/MenuDropdown/MenuDropdown";
import Button from "../../common/Button/Button";
import EditImage from "./sub-components/EditImage";

import Endpoint from "../../../lib/api/Endpoint";
import { useMutation, useImmutable } from "../../../lib/hook/SwrHooks";
import { SignedImage } from "../../../lib/types/MiscTypes";

import { IconUpload } from "../../../assets";
import { IconMore } from "../../../assets";
import { FaTrash } from "react-icons/fa";

const VALID_FILE_TYPES = ["image/png", "image/jpeg"];
const IMAGE_MENU_ITEMS = [
	{ id: "edit", displayName: "Edit", pathName: "#" },
	{
		id: "delete",
		displayName: "Delete",
		pathName: "#",
	},
];

interface ImageProps {
	src: string | SignedImage;
	onClick: (src: string | SignedImage) => void;
	selected: boolean;
	isDefault?: boolean;
	onDelete?: (src: SignedImage) => void;
	onEdit?: (src: SignedImage) => void;
}

const Image: React.FC<ImageProps> = ({
	src,
	onClick,
	selected,
	onDelete,
	isDefault,
	onEdit,
}) => {
	const [initDelete, setInitDelete] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [menuOpen, setMenuOpen] = useState(false);
	const handleSelect = useCallback(() => {
		if (deleting) {
			return;
		}

		onClick(src);
	}, [src, onClick, deleting]);

	const handleDeleteImage = useCallback(async () => {
		if (typeof src === "string" || !src.name || !onDelete || deleting) {
			return;
		}

		setDeleting(true);

		try {
			await onDelete(src);
		} catch (error) {
			setDeleting(false);
		}
	}, [onDelete, src, deleting]);

	const handleMenuItemSelect = useCallback(
		(item) => {
			if (item.id === "delete") {
				setInitDelete(true);
			}

			if (item.id === "edit") {
				if (typeof src === "string" || !onEdit) {
					return;
				}

				onEdit(src);
			}
		},
		[onEdit, src],
	);

	const onCancelDelete = useCallback(() => setInitDelete(false), []);

	const onConfirmDelete = useCallback(() => {
		if (!onDelete) {
			return;
		}

		setInitDelete(false);
		handleDeleteImage();
	}, [handleDeleteImage, onDelete]);

	const toggleMenuOpen = useCallback(() => {
		setMenuOpen(!menuOpen);
	}, [menuOpen]);

	const srcName = typeof src === "string" ? src : src.name;
	const name = srcName.substring(srcName.lastIndexOf("/") + 1);

	return (
		<div
			className={clsx("ImageSelector__modal-content-image-wrapper", {
				"--deleting": deleting,
				"--default": isDefault,
			})}
		>
			{initDelete ? (
				<div className={"ImageSelector__modal-content-image-confirm-delete"}>
					<p>Are you sure you want to delete this image?</p>
					<div
						className={
							"ImageSelector__modal-content-image-confirm-delete-actions"
						}
					>
						<Button
							onClick={onConfirmDelete}
							small={true}
							icon={<FaTrash />}
							className={"ImageSelector__modal-content-btn-confirm-delete"}
						>
							Confirm
						</Button>
						<Button onClick={onCancelDelete} small={true} outlined={true}>
							Cancel
						</Button>
					</div>
				</div>
			) : (
				<>
					{onDelete && (
						<div
							className={"ImageSelector__modal-content-image-options-container"}
						>
							<MenuDropdown
								visible={menuOpen}
								onVisibleChange={setMenuOpen}
								placement={"bottom"}
								preventOverflow={false}
								fullWidth={false}
								items={IMAGE_MENU_ITEMS}
								onSelect={handleMenuItemSelect}
							>
								<IconMore
									className={"ImageSelector__modal-content-image-options"}
									onClick={toggleMenuOpen}
								/>
							</MenuDropdown>
						</div>
					)}
					<div
						className={clsx("ImageSelector__modal-content-image", {
							"--selected": selected,
						})}
						onClick={handleSelect}
					>
						<img src={typeof src === "string" ? src : src.src} alt={""} />
					</div>
					<p>{name}</p>
				</>
			)}
		</div>
	);
};

interface ImageSelectorProps {
	value?: string | SignedImage;
	unsignedValue?: string;
	name?: string;
	defaultImages?: Array<string | SignedImage>;
	onChange: (value: string | SignedImage) => void;
	customImageAPI: Endpoint;
	imageUploadAPI: Endpoint;
	imageDeleteAPI: Endpoint;
	imagePath?: string;
	disabled?: boolean;
}

const ImageSelector: React.FC<ImageSelectorProps> = (props) => {
	const {
		value,
		unsignedValue,
		defaultImages,
		onChange,
		customImageAPI,
		imageUploadAPI,
		imageDeleteAPI,
		imagePath,
		disabled,
	} = props;
	const [modalActive, setModalActive] = useState(false);
	const [uploadError, setUploadError] = useState(false);
	const [editingImage, setEditingImage] = useState<SignedImage | null>(null);
	const swrKey = `${customImageAPI?.id}-${imagePath}`;
	const {
		data: images,
		mutate: updateImages,
		...imageState
	} = useImmutable(
		swrKey,
		async () => {
			const res = await customImageAPI?.submit({ query: { path: imagePath } });
			return res?.data;
		},
		[],
	);
	const { trigger: uploadImage, isMutating: imageUploading } = useMutation(
		[imageUploadAPI?.path, imagePath],
		imageUploadAPI,
	);
	const { trigger: deleteImage, isMutating: isDeleting } = useMutation(
		[imageDeleteAPI?.path, imagePath],
		imageDeleteAPI,
	);

	const toggleModal = useCallback(() => {
		if (!modalActive && disabled) {
			return;
		}

		setModalActive(!modalActive);
	}, [modalActive, disabled]);

	const handleSelect = useCallback(
		(src: string | SignedImage) => {
			onChange(src);
			setModalActive(false);
		},
		[onChange],
	);

	const handleImageUpload = useCallback(
		async (files: FileList | File) => {
			const file = files instanceof File ? files : files[0];

			if (!VALID_FILE_TYPES.includes(file.type)) {
				setUploadError(true);
				return;
			}

			if (uploadError) {
				setUploadError(false);
			}

			const encodedFileName = btoa(unescape(encodeURIComponent(file.name)));

			const response = await uploadImage({
				body: file,
				query: { path: imagePath },
				headers: {
					"x-original-filename": `${encodedFileName}`,
					"Content-Type": file.type,
				},
			});

			updateImages(
				async (currentData: any) => {
					return [response, ...currentData];
				},
				{ revalidate: false },
			);

			if (editingImage) {
				setEditingImage(null);
			}
		},
		[uploadError, imagePath, updateImages, uploadImage, editingImage],
	);

	const handleImageDelete = useCallback(
		async (src: SignedImage) => {
			if (!imageDeleteAPI) {
				return;
			}

			try {
				await deleteImage({
					body: { path: src.name },
				});
			} catch (error) {
				const parsedError = ErrorUtils.parseError(error);
				toast.error(parsedError.message);

				// We re-throw the error so that the image state will be updated
				throw error;
			}

			updateImages(
				async (currentData: any) => {
					return currentData.filter((image: any) => image.name !== src.name);
				},
				{ revalidate: false },
			);
		},
		[deleteImage, updateImages, imageDeleteAPI],
	);

	const handleEditImage = useCallback((image: SignedImage) => {
		setEditingImage(image);
	}, []);

	const handleCancelEdit = useCallback(() => {
		setEditingImage(null);
	}, []);

	return (
		<>
			<div className={clsx("ImageSelector", { "--disabled": disabled })}>
				<div
					className={"ImageSelector__current-image-wrapper"}
					onClick={toggleModal}
				>
					<img
						src={typeof value === "string" ? value : value?.src}
						className={"ImageSelector__current-image"}
						alt={""}
					/>

					<div className={"ImageSelector__action"}>
						<p>Replace</p>
					</div>
				</div>
			</div>

			<Modal
				visible={modalActive}
				onVisibleChange={setModalActive}
				className={"ImageSelector__modal"}
			>
				<div className={"ImageSelector__modal-content"}>
					{editingImage ? (
						<EditImage
							src={editingImage}
							onCancel={handleCancelEdit}
							onSave={handleImageUpload}
							saving={imageUploading}
						/>
					) : (
						<>
							<div className={"ImageSelector__modal-images"}>
								<h3>Default Images</h3>
								<div className={"ImageSelector__modal-content-default-images"}>
									{defaultImages?.map((image, index) => (
										<Image
											key={index}
											src={image}
											isDefault={true}
											onClick={handleSelect}
											selected={image === unsignedValue}
										/>
									))}
								</div>

								<h3>Uploaded Images</h3>
								<div
									className={clsx(
										"ImageSelector__modal-content-default-images",
										{
											"--loading": imageState.isLoading,
										},
									)}
								>
									{imageState.isLoading && <LoadingSpinner />}
									{imageUploading && (
										<div
											className={
												"ImageSelector__modal-content-image-wrapper --skeleton"
											}
										>
											<div className={"ImageSelector__modal-content-image"} />
										</div>
									)}
									{images?.map((image: any) => (
										<Image
											key={image.name || image}
											src={image}
											onClick={handleSelect}
											onDelete={imageDeleteAPI ? handleImageDelete : undefined}
											selected={
												(typeof image === "string" ? image : image.name) ===
												unsignedValue
											}
											onEdit={handleEditImage}
										/>
									))}
								</div>
							</div>
							{imageUploadAPI && (
								<div className={"ImageSelector__modal-actions"}>
									<DragAndDropFile
										accept={VALID_FILE_TYPES}
										placeholder={"Upload a new image"}
										icon={<IconUpload />}
										onAfterDrop={handleImageUpload}
										showError={uploadError}
										errorMessage={"Inavlid file type"}
									/>
								</div>
							)}
						</>
					)}
				</div>
			</Modal>
		</>
	);
};

export default ImageSelector;
